import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, OfflineError } from '../api/http';
import {
  fetchCommands,
  fetchSession,
  fetchSessionEvents,
  fetchVotes,
  subscribeSession,
  type StreamMessage,
  type CommandView,
  type SessionEvent,
  type SessionFull,
  type SessionView,
  type VoteView,
  type Definition,
  type Objective,
} from '../api/missions';
import { ca } from '../i18n/ca';

export function errorText(err: unknown): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  if (!(err instanceof ApiError)) return ca.errors.unknown;
  // A `not_possible` whose detail is itself a known code ("server_has_players")
  // reads as that code; any other detail is what the platform or the panel
  // said, and follows the sentence instead of being thrown away.
  const detail = err.detail;
  // A refusal for people on the server says how many are on it, when the platform knows.
  const code = detail === 'server_has_players' ? detail : err.code;
  if (code === 'server_has_players') {
    const players = (err.body as { players?: unknown } | null)?.players;
    if (typeof players === 'number' && players > 0) return ca.serverHasPlayersCount(players);
  }
  if (detail && ca.errors[detail]) return ca.errors[detail];
  const base = ca.errors[err.code] ?? ca.errors.unknown;
  return detail ? `${base} ${detail}` : base;
}

/**
 * Whether a failed request means the platform could not be reached, as opposed
 * to an answer it gave: a network error, a proxy's own error page that is not
 * JSON, or a 5xx. A 404 or a 403 is an answer, and the page says what it was.
 */
export function unreachable(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status >= 500;
}

/**
 * Everything a session page or the direction page needs, kept fresh: the
 * session, its events and the votes. Updates arrive over the
 * SSE stream when the browser has one; otherwise, and as a safety net while
 * the session is live, the hook polls every ten seconds.
 */
export function useSessionLive(code: string, view?: SessionView) {
  const [data, setData] = useState<SessionFull | null>(null);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [votes, setVotes] = useState<VoteView[]>([]);
  const [commands, setCommands] = useState<CommandView[]>([]);
  const [error, setError] = useState<string | null>(null);
  /**
   * The platform answered 404 for this code: no such night, or one the reader
   * may not see. It answers the two alike (a private mission's night is not
   * revealed), and so does the page.
   */
  const [unavailable, setUnavailable] = useState(false);
  const [lastReceivedAt, setLastReceivedAt] = useState<number | null>(null);
  /**
   * Whether the last request to the platform failed. The polls used to swallow
   * their errors, so a phone that lost the platform kept showing the last state
   * as if it were live (UI doc 7: the page says so and keeps the last state).
   */
  const [failing, setFailing] = useState(false);
  const [online, setOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine !== false,
  );
  const lastSeq = useRef(-1);

  const loadEvents = useCallback(async () => {
    let more: SessionEvent[];
    try {
      more = await fetchSessionEvents(code, lastSeq.current, view);
    } catch (err) {
      setFailing(unreachable(err));
      throw err;
    }
    setFailing(false);
    setLastReceivedAt(Date.now());
    if (more.length) {
      lastSeq.current = Math.max(lastSeq.current, more[more.length - 1].seq);
      // Two requests can be in flight at once (the poll, the stream's own
      // message, a probe after the stream dropped), and both ask from the same
      // seq: an event is appended once, or a support granted once counts twice.
      setEvents((list) => {
        const last = list.length ? list[list.length - 1].seq : -1;
        const fresh = more.filter((e) => e.seq > last);
        return fresh.length ? [...list, ...fresh] : list;
      });
    }
    return more;
  }, [code, view]);

  const loadSession = useCallback(async () => {
    const d = await fetchSession(code, view);
    setData(d);
    return d;
  }, [code, view]);

  const loadVotes = useCallback(
    () =>
      fetchVotes(code)
        .then(setVotes)
        .catch(() => undefined),
    [code],
  );

  /** The command queue is the director's; anyone else gets a 403 the hook swallows, so it is only asked for when it can be shown. */
  const loadCommands = useCallback(
    () =>
      fetchCommands(code)
        .then(setCommands)
        .catch(() => undefined),
    [code],
  );

  const refresh = useCallback(async () => {
    try {
      const d = await loadSession();
      const more = await loadEvents();
      if (more.some((e) => e.kind === 'missionEnded')) await loadSession();
      await Promise.all([
        loadVotes(),
        view === 'direction' && d.canDirect ? loadCommands() : undefined,
      ]);
      setError(null);
      setUnavailable(false);
    } catch (err) {
      setError(errorText(err));
      setUnavailable(err instanceof ApiError && err.status === 404);
      setFailing(unreachable(err));
    }
  }, [loadSession, loadEvents, loadVotes, loadCommands, view]);

  useEffect(() => {
    lastSeq.current = -1;
    setEvents([]);
    void refresh();
  }, [refresh]);

  // The stream is opened once per session code and nothing else: it used to
  // hang off the session's own state, so every load closed and reopened it two
  // or three times over, and the server aborted the ones it had just opened
  // (Caddy turned each into a 503 the page then had to recover from,
  // 2026-09-08). The handler is read through a ref, so it always runs the
  // latest closure without the subscription depending on it.
  const onStream = useRef<(m: StreamMessage) => void>(() => undefined);
  onStream.current = (m: StreamMessage) => {
    if (m.type === 'vote') void loadVotes();
    else if (m.type === 'briefing' || m.type === 'roster') void loadSession();
    else {
      if (m.type === 'command' && view === 'direction' && data?.canDirect) void loadCommands();
      void loadEvents()
        .then((more) => {
          if (m.type === 'state' || more.some((e) => e.kind === 'missionEnded')) void loadSession();
        })
        .catch(() => undefined);
    }
  };

  // A stream that errors may only be reconnecting (the browser retries by
  // itself); one poll right away tells a hiccup from a platform that is gone.
  // (Re)opened, it asks for whatever happened while it was down.
  const onStreamError = useRef<() => void>(() => undefined);
  onStreamError.current = () => void loadEvents().catch(() => undefined);

  // The browser knows before any request does: offline is disconnected at
  // once, and back online asks for everything missed.
  useEffect(() => {
    const down = () => setOnline(false);
    const up = () => {
      setOnline(true);
      void refresh();
    };
    window.addEventListener('offline', down);
    window.addEventListener('online', up);
    return () => {
      window.removeEventListener('offline', down);
      window.removeEventListener('online', up);
    };
  }, [refresh]);

  const [subscribed, setSubscribed] = useState(false);
  useEffect(() => {
    const unsubscribe = subscribeSession(
      code,
      (m) => onStream.current(m),
      () => onStreamError.current(),
      () => onStreamError.current(),
    );
    setSubscribed(!!unsubscribe);
    return () => {
      unsubscribe?.();
      setSubscribed(false);
    };
  }, [code]);

  // The safety net: every ten seconds while the session is live, always when
  // the browser has no EventSource at all, and while the platform cannot be
  // reached, live or not, stream or not: the disconnected banner says the page
  // tries again by itself, and a stream the server closed opens only every so
  // often. Failing, it asks for everything, the session included, since a page
  // that never loaded has nothing to catch up from.
  useEffect(() => {
    const live = data?.session.state === 'LIVE';
    if (subscribed && !live && !failing) return undefined;
    const timer = setInterval(() => {
      if (failing) {
        void refresh();
        return;
      }
      void loadEvents()
        .then((more) => {
          if (more.some((e) => e.kind === 'missionEnded')) void loadSession();
        })
        .catch(() => undefined);
      void loadVotes();
      if (view === 'direction' && data?.canDirect) void loadCommands();
    }, 10000);
    return () => clearInterval(timer);
  }, [
    subscribed,
    failing,
    refresh,
    data?.session.state,
    data?.canDirect,
    view,
    loadEvents,
    loadSession,
    loadVotes,
    loadCommands,
  ]);

  return {
    data,
    setData,
    events,
    votes,
    setVotes,
    commands,
    setCommands,
    error,
    unavailable,
    setError,
    refresh,
    lastReceivedAt,
    failing,
    online,
  };
}

/** Objectives the direction added while the mission ran (`objectiveAdded` events), in the definition's shape. */
export function addedObjectivesOf(events: SessionEvent[]): Objective[] {
  return events
    .filter((e) => e.kind === 'objectiveAdded')
    .map((e) => {
      const s = (e.payload.server ?? {}) as Record<string, unknown>;
      return {
        id: String(s.id ?? ''),
        type: String(s.type ?? 'clear_area'),
        title: typeof s.title === 'string' ? s.title : undefined,
        position: Array.isArray(s.position) ? (s.position as number[]) : [0, 0],
        radius: typeof s.radius === 'number' ? s.radius : 250,
        priority: (s.priority as Objective['priority']) ?? 'secondary',
        dependsOn: Array.isArray(s.dependsOn) ? (s.dependsOn as string[]) : [],
      };
    })
    .filter((o) => o.id !== '');
}

/** The definition with the objectives added at runtime appended once. */
export function withAdded(definition: Definition, events: SessionEvent[]): Definition {
  const added = addedObjectivesOf(events).filter(
    (a) => !definition.objectives.some((o) => o.id === a.id),
  );
  const withObjectives =
    added.length === 0
      ? definition
      : { ...definition, objectives: [...definition.objectives, ...added] };
  return withPlacements(withObjectives, events);
}

/**
 * Where things actually are. The runtime re-checks every placement against the
 * ground when the mission starts and moves what the terrain refuses — an
 * extraction point in the sea, an observation post with nothing in sight — and
 * says so with a `placementResolved` event. The map a player or a director
 * looks at has to show the point the server chose, not the one the generator
 * guessed, or it is sending people to the wrong field.
 */
export function withPlacements(definition: Definition, events: SessionEvent[]): Definition {
  const moves = placementsOf(events);
  if (moves.size === 0) return definition;
  const next: Definition = { ...definition };
  const extraction = moves.get('extraction');
  if (extraction) {
    next.points = {
      ...(next.points ?? {}),
      extraction: { ...(next.points?.extraction ?? {}), position: extraction.position },
    };
    next.markers = (next.markers ?? []).map((m) =>
      m.id === 'extraction' ? { ...m, position: extraction.position } : m,
    );
  }
  const insertion = moves.get('insertion');
  if (insertion && insertion.movedBy > 0) {
    next.points = {
      ...(next.points ?? {}),
      insertion: { ...(next.points?.insertion ?? {}), position: insertion.position },
    };
  }
  next.objectives = next.objectives.map((o) => {
    const moved = moves.get(`objective:${o.id}`);
    return moved ? { ...o, position: moved.position } : o;
  });
  return next;
}

export interface Placement {
  id: string;
  position: [number, number];
  movedBy: number;
  reason: string;
}

/** Every placement the runtime resolved, latest first wins. */
export function placementsOf(events: SessionEvent[]): Map<string, Placement> {
  const out = new Map<string, Placement>();
  events
    .filter((e) => e.kind === 'placementResolved')
    .forEach((e) => {
      const s = (e.payload.server ?? {}) as Record<string, unknown>;
      const p = s.position as number[] | undefined;
      if (!Array.isArray(p) || p.length < 2) return;
      out.set(String(s.id), {
        id: String(s.id),
        position: [Number(p[0]), Number(p[1])],
        movedBy: Number(s.movedBy ?? 0),
        reason: String(s.reason ?? ''),
      });
    });
  return out;
}

/** One row of the runtime's placement report: what stood where, and what was under it. */
export interface PlacementRow {
  kind: 'object' | 'slot' | 'vehicle' | 'force' | 'point' | string;
  id: string;
  className: string;
  asked: number[];
  placed: number[];
  under: number;
  gap: number;
  outcome: string;
  note: string;
  /** What holds it up: floor | piece | wall | authored, or '' when the runtime did not say. */
  stance: string;
  /** The thing it stands on, rests on or hangs on: a class, 'the terrain', 'the sea', or ''. */
  on: string;
}

export interface PlacementReport {
  rows: PlacementRow[];
  total: number;
  off: number;
  water: number;
}

/**
 * The last placement report the runtime sent (`placementReport`, one per start),
 * its rows read out of the compact arrays the event carries:
 * [kind, id, class, asked, placed, under, gap, outcome, note, on] — `on`
 * ([stance, what, metres], since 2026-09-26) is optional and older reports lack it.
 */
export function placementReportOf(events: SessionEvent[]): PlacementReport | null {
  const last = [...events].reverse().find((e) => e.kind === 'placementReport');
  if (!last) return null;
  const s = (last.payload.server ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(s.rows) ? (s.rows as unknown[]) : [];
  const rows: PlacementRow[] = raw
    .filter((r): r is unknown[] => Array.isArray(r) && r.length >= 8)
    .map((r) => ({
      kind: String(r[0]),
      id: String(r[1] ?? ''),
      className: String(r[2] ?? ''),
      asked: Array.isArray(r[3]) ? (r[3] as number[]).map(Number) : [],
      placed: Array.isArray(r[4]) ? (r[4] as number[]).map(Number) : [],
      under: Number(r[5] ?? 0),
      gap: Number(r[6] ?? 0),
      outcome: String(r[7] ?? ''),
      note: String(r[8] ?? ''),
      stance: Array.isArray(r[9]) && r[9].length >= 2 ? String((r[9] as unknown[])[0] ?? '') : '',
      on: Array.isArray(r[9]) && r[9].length >= 2 ? String((r[9] as unknown[])[1] ?? '') : '',
    }));
  const counts = (s.counts ?? {}) as Record<string, unknown>;
  return {
    rows,
    total: Number(counts.total ?? rows.length),
    off: Number(
      counts.off ??
        rows.filter((r) => ['floating', 'sunk', 'destroyed', 'clipping'].includes(r.outcome))
          .length,
    ),
    water: Number(counts.water ?? rows.filter((r) => r.outcome === 'water').length),
  };
}

/** The night's clock, when the definition set one and the runtime has reported on it. */
export function clockOf(events: SessionEvent[]): { remaining: number; phase: string } | null {
  const last = [...events].reverse().find((e) => e.kind === 'clock');
  if (!last) return null;
  const s = (last.payload.server ?? {}) as Record<string, unknown>;
  return { remaining: Number(s.remaining ?? 0), phase: String(s.phase ?? '') };
}

export function objectiveStatesOf(events: SessionEvent[]) {
  const states = new Map<
    string,
    { state: string; title: string; type?: string; position?: number[] }
  >();
  events
    .filter((e) => e.kind === 'objectiveState')
    .forEach((e) => {
      const id = String(e.payload.server?.id);
      const before = states.get(id);
      const at = e.payload.server?.position;
      const type = e.payload.server?.type;
      // The place and the kind come with the `active` event, the one that gives the
      // objective; the later ones (done, failed) keep what was already said, since a
      // hidden objective's own place is not in a player's definition.
      states.set(id, {
        state: String(e.payload.server?.state),
        title: String(e.payload.server?.title ?? ''),
        type: typeof type === 'string' ? type : before?.type,
        position: Array.isArray(at) && at.length >= 2 ? (at as number[]) : before?.position,
      });
    });
  return states;
}

export function describeEvent(e: SessionEvent): string {
  const s = (e.payload.server ?? {}) as Record<string, unknown>;
  switch (e.kind) {
    case 'objectiveState':
      return `${String(s.title ?? s.id)} · ${String(s.state)}`;
    case 'health':
      return `${Number(s.fps ?? 0).toFixed(0)} fps · ${String(s.players ?? 0)} jugadors · ${String(s.aiUnits ?? 0)} IA`;
    case 'casualty':
      return `${String(s.victim)}${s.player ? ' (jugador)' : ''}`;
    case 'supportUsed':
      return `${String(s.support)} ${Number(s.delta ?? 0) > 0 ? '+' : ''}${String(s.delta ?? '')}`;
    case 'missionEnded':
      return String(s.outcome ?? '');
    case 'commandAck':
      return `${String(s.kind)} ${s.ok ? '✓' : '✗'}`;
    case 'placementResolved':
      return Number(s.movedBy ?? 0) > 0
        ? `${String(s.id)} mogut ${String(s.movedBy)} m (${String(s.reason)})`
        : `${String(s.id)} confirmat${s.reason ? ` · ${String(s.reason)}` : ''}`;
    case 'built':
      return `${String(s.objectives ? (s.objectives as unknown[]).length : 0)} objectius, ${String(s.forces ?? 0)} elements, ${String(s.slots ?? 0)} places`;
    case 'placementReport': {
      const c = (s.counts ?? {}) as Record<string, unknown>;
      return `${String(c.total ?? 0)} files · ${String(c.off ?? 0)} fora de lloc · ${String(c.water ?? 0)} a l’aigua`;
    }
    case 'scenePlacement': {
      const c = (s.counts ?? {}) as Record<string, unknown>;
      return `${String(s.scene ?? '')}: ${String(c.total ?? 0)} files · ${String(c.off ?? 0)} fora de lloc · ${String(c.water ?? 0)} a l’aigua`;
    }
    case 'placementPlan': {
      const c = (s.counts ?? {}) as Record<string, unknown>;
      return `${String(s.composition ?? '')} (${s.phase === 'planned' ? 'abans de construir' : 'construït'}): ${String(c.total ?? 0)} entrades · ${String(c.moved ?? 0)} en un altre lloc · ${String(c.notMade ?? 0)} sense fer`;
    }
    case 'ordersTaken':
      return s.by ? `per ${String(s.by)}` : 'pel rellotge';
    case 'clock':
      return `${String(s.phase ?? '')} · ${Math.round(Number(s.remaining ?? 0) / 60)} min`;
    case 'script':
      return `${ca.scriptPhases.event(String(s.name ?? ''))}${s.alarm === 'up' ? ` · ${ca.scriptPhases.alarmUp}` : ''}`;
    case 'log':
      return s.radio ? `${String(s.from)}: ${String(s.radio)}` : JSON.stringify(s);
    case 'serverDown':
      return 'El panell diu que el servidor està aturat; ningú s’hi pot connectar.';
    case 'restartLoop':
      return `${String(s.starts ?? '?')} arrencades seguides sense que el runtime informés${
        s.stopped ? '; el servidor s’ha aturat' : ''
      }. Revisa la missió i torna a arrencar la sessió.`;
    default:
      return '';
  }
}
